import React, { useState } from 'react';
import { DatePicker, Input, Button, Select, Checkbox, Modal } from 'antd';
import styles from './advanced-filters.module.scss';
import { useSelector } from 'react-redux';
import { setAdvancedFilters, toggleAdvancedFilter } from 'redux/spot/spot-actions';
import gradesArray from 'util/grades-array';
import { useLeadSources } from 'components/pages/crm-spot/hooks/useLeadSources';
import { useLeadSubStages } from 'components/pages/crm-spot/hooks/useLeadSubStages';

export const AdvancedFilters = ({ schoolId }) => {
  const { isAdvancedFilterOpen } = useSelector((state) => state.spot);

  const initialFilters = {
    createdAt_from: '',
    createdAt_to: '',
    grades: [],
    search_notes: '',
    year: '',
    language: '',
    lead_source_id: null,
    lead_status_id: null,
  };

  const [filters, setFilters] = useState(initialFilters);

  const { sources } = useLeadSources(schoolId);
  const { statuses: subStages } = useLeadSubStages(schoolId);

  const handleFilter = (key, value) => {
    setFilters((prevFilters) => {
      const currentValue = prevFilters[key];
      const isArray = Array.isArray(currentValue);

      if (!isArray) {
        return {
          ...prevFilters,
          [key]: value,
        };
      }

      const updatedArray = currentValue.includes(value)
        ? currentValue.filter((item) => item !== value)
        : [...currentValue, value];

      return {
        ...prevFilters,
        [key]: updatedArray,
      };
    });
  };

  // Generate dynamic school years starting from 2023-2024 up to 3 years ahead from current year
  const getOptionYears = () => {
    const startYear = 2020;
    const currentYear = new Date().getFullYear();
    const years = [];
    // Always show at least from 2023-2024 up to current year + 3
    const endYear = Math.max(currentYear + 4, startYear + 4);
    for (let y = startYear; y <= endYear; y++) {
      years.push({ value: `${y}-${y + 1}`, label: `${y}-${y + 1}` });
    }
    return years;
  };
  const optionYears = getOptionYears();

  const onResetFilters = () => {
    setAdvancedFilters(initialFilters);
    toggleAdvancedFilter();
  };

  const onApplyFilters = () => {
    setAdvancedFilters(filters);
    toggleAdvancedFilter();
  };

  return (
    <Modal
      title="Filters"
      open={isAdvancedFilterOpen}
      onCancel={toggleAdvancedFilter}
      footer={[
        <Button onClick={onResetFilters}>Reset Filters</Button>,
        <Button onClick={onApplyFilters} type="primary">
          Apply Filters
        </Button>,
      ]}>
      <div className={styles.advancedFiltersContainer}>
        <div>
          <label className={styles.filterLabel}>Grades</label>
          <div className={styles.filterGrid}>
            {gradesArray.map((grade) => {
              let label;
              if (grade === 'Pre-K' || grade === 'TK' || grade === 'HS' || grade === 'K') {
                label = grade;
              } else {
                // Add ordinal suffixes for 1st, 2nd, 3rd, 4th, etc.
                const n = parseInt(grade, 10);
                if (!isNaN(n)) {
                  let suffix = 'th';
                  if (n === 1) suffix = 'st';
                  else if (n === 2) suffix = 'nd';
                  else if (n === 3) suffix = 'rd';
                  else if (n > 3 && n < 21) suffix = 'th';
                  else if (n % 10 === 1) suffix = 'st';
                  else if (n % 10 === 2) suffix = 'nd';
                  else if (n % 10 === 3) suffix = 'rd';
                  label = `${n}${suffix} Grade`;
                } else {
                  label = grade;
                }
              }
              return (
                <div key={grade} className={styles.filterCheckbox}>
                  <Checkbox
                    id={`grade-${grade}`}
                    checked={filters.grades.includes(grade)}
                    onChange={() => handleFilter('grades', grade)}>
                    {label}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.filterGroup}>
          <div>
            <label htmlFor="year" className={styles.filterLabel}>
              Lead Year
            </label>
            <Select
              id="year"
              placeholder="Select Year"
              value={filters.year}
              onChange={(value) => handleFilter('year', value)}
              className={styles.filterSelect}
              options={optionYears}
            />
          </div>

          {/* <div className={`${styles.filterItem}`}>
          </div> */}
          {schoolId && (
            <>
              <div>
                <label htmlFor="source" className={styles.filterLabel}>
                  Source
                </label>
                <Select
                  id="source"
                  value={filters.source}
                  onChange={(value) => handleFilter('lead_source_id', value)}
                  className={styles.filterSelect}
                  options={
                    sources?.map((source) => ({
                      value: source.id,
                      label: source.name,
                    })) || []
                  }
                  placeholder="Select Lead Source"
                />
              </div>
              <div>
                <label htmlFor="subStages" className={styles.filterLabel}>
                  Sub Stage
                </label>
                <Select
                  id="subStages"
                  placeholder="Select Lead Sub Stage"
                  value={filters.source}
                  onChange={(value) => handleFilter('lead_status_id', value)}
                  className={styles.filterSelect}
                  options={
                    subStages?.map((stage) => ({
                      value: stage.id,
                      label: stage.name,
                    })) || []
                  }
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="language" className={styles.filterLabel}>
              Language
            </label>
            <Select
              id="language"
              placeholder="Select Year"
              value={filters.language}
              onChange={(value) => handleFilter('language', value)}
              className={styles.filterSelect}
              options={[
                { value: '', label: 'All Languages' },
                { value: 'english', label: 'English' },
                { value: 'spanish', label: 'Spanish' },
              ]}
            />
          </div>
        </div>

        <div>
          <label className={styles.filterLabel}>Date Added</label>
          <div className={styles.filterRow}>
            <div className={styles.filterItem}>
              <label htmlFor="dateFrom" className={styles.filterSubLabel}>
                Date Added From
              </label>
              <DatePicker id="dateFrom" onChange={(date, dateString) => handleFilter('createdAt_from', dateString)} />
            </div>
            <div className={styles.filterItem}>
              <label htmlFor="dateTo" className={styles.filterSubLabel}>
                Date Added To
              </label>
              <DatePicker id="dateTo" onChange={(date, dateString) => handleFilter('createdAt_to', dateString)} />
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="search_notes" className={styles.filterLabel}>
            Search in Notes
          </label>
          <Input.TextArea
            id="search_notes"
            placeholder="Search for keywords in lead notes..."
            value={filters.search_notes}
            onChange={(e) => handleFilter('search_notes', e.target.value)}
            className={styles.filterTextarea}
          />
        </div>
      </div>
    </Modal>
  );
};
